import { Component, Inject, OnInit, DestroyRef, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { SpecialtiesService } from '../../services/specialties/specialties.service';
import { Specialty } from '../../models/specialty.model';

export interface SpecialtyDialogData {
  id?: number | string;
  name_ar?: string;
  name_en?: string;
  description_ar?: string;
  description_en?: string;
}

@Component({
    selector: 'app-specialty-form',
    templateUrl: './specialty-form.component.html',
    styleUrls: ['./specialty-form.component.scss'],
    standalone: false
})
export class SpecialtyFormComponent implements OnInit {
  isEditMode = false;
  submitted = false;
  saving = false;
  apiError = '';

  specialtyFormGroup = new FormGroup({
    name_en: new FormControl('', Validators.required),
    name_ar: new FormControl('', Validators.required),
    description_ar: new FormControl('', Validators.required),
    description_en: new FormControl('', Validators.required),
  });

  private destroyRef = inject(DestroyRef);

  constructor(
    public dialogRef: MatDialogRef<SpecialtyFormComponent>,
    @Inject(MAT_DIALOG_DATA) public data: SpecialtyDialogData,
    private specialtiesService: SpecialtiesService,
  ) {}

  ngOnInit(): void {
    this.isEditMode = this.data?.id !== undefined && this.data?.id !== null;
    if (this.isEditMode) {
      this.specialtyFormGroup.patchValue({
        name_en: this.data.name_en ?? '',
        name_ar: this.data.name_ar ?? '',
        description_ar: this.data.description_ar ?? '',
        description_en: this.data.description_en ?? '',
      });
    }
  }

  save(): void {
    this.submitted = true;
    this.apiError = '';

    if (this.specialtyFormGroup.invalid) {
      this.specialtyFormGroup.markAllAsTouched();
      return;
    }

    const payload = {
      name_ar: this.specialtyFormGroup.value.name_ar ?? '',
      name_en: this.specialtyFormGroup.value.name_en ?? '',
      description_ar: this.specialtyFormGroup.value.description_ar ?? '',
      description_en: this.specialtyFormGroup.value.description_en ?? '',
    };

    this.saving = true;
    const request$ = this.isEditMode
      ? this.specialtiesService.updateSpecialty(this.data.id as number | string, payload)
      : this.specialtiesService.createSpecialty(payload);

    request$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (result: Specialty) => {
        this.saving = false;
        this.dialogRef.close(result);
      },
      error: (error) => {
        this.saving = false;
        this.handleBackendError(error);
      },
    });
  }

  private handleBackendError(error: any): void {
    const errors = error?.error;
    if (errors?.name_ar) {
      this.specialtyFormGroup.get('name_ar')?.setErrors({ backend: errors.name_ar });
    }
    if (errors?.name_en) {
      this.specialtyFormGroup.get('name_en')?.setErrors({ backend: errors.name_en });
    }
    if (errors?.description_ar) {
      this.specialtyFormGroup.get('description_ar')?.setErrors({ backend: errors.description_ar });
    }
    if (errors?.description_en) {
      this.specialtyFormGroup.get('description_en')?.setErrors({ backend: errors.description_en });
    }
    const hasFieldError = errors?.name_ar || errors?.name_en || errors?.description_ar || errors?.description_en;
    this.apiError = errors?.detail || (!hasFieldError ? 'حدث خطأ أثناء الحفظ' : '');
  }

  cancel(): void {
    this.dialogRef.close();
  }
}
